import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ProfilePicture from './shared/ProfilePicture';
import {
  XMarkIcon,
  UserPlusIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

interface Guest {
  mg_nr: number;
  vorname: string;
  name: string;
  has_booked?: boolean;
  cards?: Array<{
    id: number;
    remaining_entries: number;
  }>;
}

interface AddGuestModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
  onBook: (eventId: number, count: number) => void;
  checkBooking?: () => void;
  refreshEvent?: () => void;
}

const AddGuestModal: React.FC<AddGuestModalProps> = ({ 
  event, 
  isOpen, 
  onClose, 
  onBook, 
  checkBooking,
  refreshEvent 
}) => {
  const { config } = useContext(AuthContext);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loading, setLoading] = useState({
    fetchGuests: false,
    addGuest: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, onClose]);

  // Fetch guests with cards
  const fetchGuests = async () => {
    if (!event) return;
    setLoading(prev => ({ ...prev, fetchGuests: true }));
    try {
      const response = await apiClient.get(`/api/guest-cards/guests-with-cards?eventId=${event.id}`);
      if (response.data.success) {
        setGuests(response.data.guests || []);
      } else {
        setError('Keine Gäste gefunden');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(`Fehler beim Laden der Gäste: ${err.message}`);
      }
    } finally {
      setLoading(prev => ({ ...prev, fetchGuests: false }));
    }
  };

  const handleAddMember = async () => {
    setLoading(prev => ({ ...prev, addGuest: true }));
    const memberId = Number(selectedMember);
    const guest = guests.find(g => g.mg_nr === memberId);
    
    try {
      if (guest && guest.cards && guest.cards.length > 0) {
        const availableCard = guest.cards.find(card => card.remaining_entries > 0);
        if (!availableCard) {
          setError('Keine verfügbaren Tickets auf der Gästekarte');
          return;
        }
        await handleGuestBooking(memberId, availableCard);
      } else {
        setError('Kein Gast oder keine Gästekarte verfügbar');
      }
    } finally {
      setLoading(prev => ({ ...prev, addGuest: false }));
    }
  };

  const handleGuestBooking = async (memberId: number, guestCard: any) => {
    try {
      const response = await apiClient.post('/api/members/book-event', {
        guest_card_id: guestCard.id,
        kalender_id: event.id,
        guest_id: memberId
      });
      
      if (response.data.success) {
        const guest = guests.find(g => g.mg_nr === memberId);
        setSuccessMessage(`Termin für ${guest?.vorname} ${guest?.name} erfolgreich gebucht!`);
        setSelectedMember('');
        
        if (checkBooking) await checkBooking();
        if (refreshEvent) await refreshEvent();
        
        const bookingCount = response.data.booking_count || event.extendedProps?.bookingCount || 0;
        onBook(event.id, Math.min(bookingCount, event.extendedProps?.bookable || bookingCount));
      } else {
        setError(response.data.message || 'Buchung fehlgeschlagen');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(`Fehler beim Buchen: ${err.message}`);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGuests();
    }
  }, [isOpen, event]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Animation effect
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <UserPlusIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Gast hinzufügen</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <ExclamationCircleIcon className="h-5 w-5 text-error-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-error-700 dark:text-error-300">{error}</span>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-success-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-success-700 dark:text-success-300">{successMessage}</span>
                </div>
              </div>
            )}

            {/* Guest Selection */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <UserIcon className="h-4 w-4" />
                Verfügbare Gäste auswählen:
              </p>
              
              {loading.fetchGuests ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Gäste werden geladen...</span>
                </div>
              ) : guests.length === 0 ? (
                <div className="text-center py-12">
                  <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Keine Gäste verfügbar</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Alle Gäste haben bereits gebucht oder keine gültigen Karten
                  </p>
                </div>
              ) : (
                <div className="max-h-[40vh] overflow-y-auto">
                  <div className="space-y-3">
                    {guests.map((guest) => {
                      const hasAvailableTickets = guest.cards && guest.cards.length > 0 && 
                        guest.cards.some(card => card.remaining_entries > 0);
                      const totalTickets = guest.cards ? 
                        guest.cards.reduce((sum, card) => sum + card.remaining_entries, 0) : 0;
                      const isDisabled = guest.has_booked || !hasAvailableTickets;
                      const isSelected = selectedMember === guest.mg_nr.toString();
                      
                      return (
                        <button
                          key={guest.mg_nr}
                          onClick={() => !isDisabled && setSelectedMember(guest.mg_nr.toString())}
                          disabled={isDisabled}
                          className={`w-full min-h-[56px] flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 transform ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 scale-[1.02]'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                          } ${
                            isDisabled
                              ? 'opacity-60 cursor-not-allowed'
                              : 'hover:shadow-md cursor-pointer hover:scale-[1.01] active:scale-[0.99]'
                          }`}
                        >
                          <ProfilePicture guest={guest} />
                          
                          <div className="flex-1 text-left">
                            <p className={`text-sm font-semibold ${
                              isDisabled ? 'text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {guest.vorname} {guest.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {hasAvailableTickets
                                  ? `${totalTickets} Ticket${totalTickets !== 1 ? 's' : ''} verfügbar`
                                  : guest.cards && guest.cards.length > 0
                                  ? 'Alle Tickets aufgebraucht'
                                  : 'Keine Gästekarte vorhanden'
                                }
                              </p>
                              {guest.has_booked && (
                                <span className="text-xs font-medium bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400 px-2 py-0.5 rounded-full">
                                  Bereits gebucht
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {isSelected && (
                            <CheckCircleIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={handleAddMember}
            disabled={!selectedMember || loading.addGuest}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading.addGuest ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Wird hinzugefügt...</span>
              </>
            ) : (
              <>
                <UserPlusIcon className="h-4 w-4" />
                <span>Gast hinzufügen</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddGuestModal;